import React, { useState, useEffect, useRef, useCallback } from "react";
import { getData } from "../../../Services/Services";
import "./DashboardTheme.css";
import {
  gisCoordinatesData,
  gisBlockCoordinatesData,
  gisStateCoordinates,
  findAllChildrenRegionsById,
} from "../../../Services/EndPoints";
import { postDataWithBody } from "../../../Services/Services";


// ─── Build flat options list (backward compat export) ─────────────────────────
export const buildRegionDropdownOptions = async (stateId) => {
  const options = [];
  if (!stateId) return options;
  try {
    const districtsList = await postDataWithBody(findAllChildrenRegionsById, [stateId], {});
    if (!districtsList || !Array.isArray(districtsList)) return options;
    for (const district of districtsList) {
      options.push({
        label: district.name,
        value: `district-${district.gis_id}`,
        type: "district",
        gis_id: district.gis_id,
        regionId: district.id,
        districtName: district.name,
      });
      try {
        const blocks = await postDataWithBody(findAllChildrenRegionsById, [district.id], {});
        if (blocks && Array.isArray(blocks)) {
          blocks.forEach((block) => {
            options.push({
              label: `  ${block.name}`,
              value: `block-${block.gis_id}`,
              type: "block",
              gis_id: block.gis_id,
              regionId: block.id,
              districtName: district.name,
            });
          });
        }
      } catch (_) {}
    }
  } catch (_) {}
  return options;
};

// ─── Build hierarchical tree structure ────────────────────────────────────────
export const buildRegionTree = async (stateId, stateName) => {
  const stateNode = {
    label: stateName || "STATE",
    value: `state-${stateId}`,
    type: "state",
    gis_id: stateId,
    regionId: stateId,
    children: [],
  };
  try {
    const districtsList = await postDataWithBody(findAllChildrenRegionsById, [stateId], {});
    if (!districtsList || !Array.isArray(districtsList)) return [stateNode];
    for (const district of districtsList) {
      const districtNode = {
        label: district.name,
        value: `district-${district.gis_id}`,
        type: "district",
        gis_id: district.gis_id,
        regionId: district.id,
        children: [],
      };
      try {
        const blocks = await postDataWithBody(findAllChildrenRegionsById, [district.id], {});
        if (blocks && Array.isArray(blocks)) {
          blocks.forEach((block) => {
            districtNode.children.push({
              label: block.name,
              value: `block-${block.gis_id}`,
              type: "block",
              gis_id: block.gis_id,
              regionId: block.id,
              parentGisId: district.gis_id,
              children: [],
            });
          });
        }
      } catch (_) {}
      stateNode.children.push(districtNode);
    }
  } catch (_) {}
  return [stateNode];
};

// ─── Fetch GeoJSON for a node ──────────────────────────────────────────────────
export const fetchRegionGeoJSON = async (option) => {
  if (option.type === "state") {
    const res = await getData(gisStateCoordinates + option.gis_id);
    if (!res || res.length === 0) return null;
    return {
      type: "Feature",
      properties: { name: option.label.trim(), gis_id: option.gis_id, type: "state" },
      geometry: JSON.parse(res[0].coordinates),
    };
  }
  const endpoint =
    option.type === "district"
      ? gisCoordinatesData + option.gis_id
      : gisBlockCoordinatesData + option.gis_id;
  const res = await getData(endpoint);
  if (!res || res.length === 0) return null;
  return {
    type: "Feature",
    properties: { name: option.label.trim(), gis_id: option.gis_id, type: option.type },
    geometry: JSON.parse(res[0].coordinates),
  };
};

// ─── Collect all descendant values from a node ────────────────────────────────
const collectAllValues = (node) => {
  const vals = [node.value];
  (node.children || []).forEach((c) => vals.push(...collectAllValues(c)));
  return vals;
};

// ─── Sync state checkbox based on district selection completeness ──────────────
// State is checked ↔ every district node value is present in the selected set.
const syncStateCheckbox = (nextSelected, tree) => {
  const stateNode = tree.find(n => n.type === "state");
  if (!stateNode) return;
  const districts = stateNode.children || [];
  if (districts.length === 0) return;
  const allDistrictsSelected = districts.every(d => nextSelected.has(d.value));
  if (allDistrictsSelected) {
    nextSelected.add(stateNode.value);
  } else {
    nextSelected.delete(stateNode.value);
  }
};

// ─── Helper: find parent district node for a block ────────────────────────────
const findParentDistrictInTree = (treeNodes, blockValue) => {
  for (const stateNode of treeNodes) {
    for (const distNode of (stateNode.children || [])) {
      if ((distNode.children || []).some((b) => b.value === blockValue)) {
        return distNode;
      }
    }
  }
  return null;
};

// ─── CSS ──────────────────────────────────────────────────────────────────────
const TREE_STYLES = `
  @keyframes rhtSlideIn {
    0%  { opacity:0; transform:translateY(-6px) scale(0.97); }
    100%{ opacity:1; transform:translateY(0) scale(1); }
  }
  @keyframes rhtSpin { to { transform:rotate(360deg); } }
  @keyframes rhtToastIn {
    0%  { opacity:0; transform:translateY(8px) scale(0.96); }
    100%{ opacity:1; transform:translateY(0) scale(1); }
  }
  @keyframes rhtToastOut {
    0%  { opacity:1; transform:translateY(0) scale(1); }
    100%{ opacity:0; transform:translateY(8px) scale(0.96); }
  }
  @keyframes rhtBellRing {
    0%,100%{ transform:rotate(0); }
    15%    { transform:rotate(18deg); }
    30%    { transform:rotate(-16deg); }
    45%    { transform:rotate(12deg); }
    60%    { transform:rotate(-8deg); }
    75%    { transform:rotate(5deg); }
  }

  .rht-node-row {
    display:flex; align-items:center; gap:5px;
    padding:3px 4px 3px 0;
    border-radius:5px; cursor:default;
    user-select:none; transition:background 0.12s;
  }
  .rht-node-row:hover { background:rgba(59,130,246,0.06); }

  .rht-pm-btn {
    width:16px; height:16px;
    border-radius:3px;
    border:1.5px solid var(--od-border);
    background:var(--od-surface-alt);
    display:flex; align-items:center; justify-content:center;
    flex-shrink:0; cursor:pointer;
    font-size:14px; font-weight:700; line-height:1;
    color:var(--od-text-muted);
    transition:border-color 0.12s, background 0.12s, color 0.12s;
    user-select:none;
  }
  .rht-pm-btn:hover { border-color:#3b82f6; background:#eff6ff; color:#3b82f6; }

  .rht-checkbox {
    width:14px; height:14px; border-radius:3px;
    border:1.5px solid var(--od-border); background:transparent;
    display:flex; align-items:center; justify-content:center;
    flex-shrink:0; cursor:pointer;
    transition:border-color 0.14s, background 0.14s;
  }
  .rht-checkbox:hover { border-color:#93c5fd; }
  .rht-checkbox.checked { border-color:#3b82f6; background:#3b82f6; }
  .rht-checkbox.indeterminate { border-color:#3b82f6; background:rgba(59,130,246,0.18); }

  .rht-node-label {
    flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
    cursor:pointer; transition:color 0.12s;
  }
  .rht-node-label:hover { color:#1d4ed8 !important; }
`;

// ─── TreeNode (multi-select, +/- expand, no dots) ────────────────────────────
const TreeNode = ({ node, depth, selectedSet, loadingSet, onToggle }) => {
  const hasChildren = node.children && node.children.length > 0;
  // ★ FIX: A block is also "checked" if its parent district is selected
  const isChecked   = selectedSet.has(node.value) ||
    (node.type === "block" && node.parentGisId != null && selectedSet.has(`district-${node.parentGisId}`));
  const isLoading   = loadingSet.has(node.value);

  // Indeterminate state – some but not all descendants selected
    const allDesc = hasChildren ? collectAllValues(node).slice(1) : [];
    const checkedDescCount = allDesc.filter((v) => selectedSet.has(v)).length;
    const isIndeterminate = !isChecked && checkedDescCount > 0 && checkedDescCount < allDesc.length;

    const hasSelectedDescendant = allDesc.some((v) => selectedSet.has(v));
    const [expanded, setExpanded] = useState(
        depth === 0 || isChecked || hasSelectedDescendant
    );

  const TYPE_STYLES = {
    state:    { color: "var(--od-text-primary)", weight: 700, size: 12 },
    district: { color: "var(--od-text-secondary)", weight: 600, size: 11.5 },
    block:    { color: "var(--od-text-secondary)", weight: 500, size: 11 },
  };
  const ts = TYPE_STYLES[node.type] || TYPE_STYLES.block;

  const checkboxClass = isChecked
    ? "rht-checkbox checked"
    : isIndeterminate
      ? "rht-checkbox indeterminate"
      : "rht-checkbox";

  return (
    <div style={{ paddingLeft: depth * 14 }}>
      <div className="rht-node-row">

        {/* +/- expand button — only for nodes with children */}
        {hasChildren ? (
          <div
            className="rht-pm-btn"
            onClick={(e) => { e.stopPropagation(); setExpanded((p) => !p); }}
          >
            {expanded ? "−" : "+"}
          </div>
        ) : (
          // Spacer so leaf labels align
          <div style={{ width: 16, flexShrink: 0 }} />
        )}

        {/* Checkbox */}
        <div
          className={checkboxClass}
          onClick={(e) => { e.stopPropagation(); onToggle(node, !isChecked); }}
        >
          {isLoading ? (
            <div style={{
              width: 8, height: 8, borderRadius: "50%",
              border: "1.5px solid white", borderTopColor: "transparent",
              animation: "rhtSpin 0.7s linear infinite",
            }} />
          ) : isChecked ? (
            <svg width="8" height="6" viewBox="0 0 9 7" fill="none">
              <path d="M1 3.5L3.5 6L8 1" stroke="white" strokeWidth="1.8"
                strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : isIndeterminate ? (
            <div style={{ width: 8, height: 2, background: "#3b82f6", borderRadius: 1 }} />
          ) : null}
        </div>

        {/* Label (click = toggle checkbox) */}
        <span
          className="rht-node-label"
          style={{
            fontSize: ts.size,
            fontWeight: isChecked ? 700 : ts.weight,
            color: isChecked ? "#1d4ed8" : ts.color,
          }}
          onClick={(e) => { e.stopPropagation(); onToggle(node, !isChecked); }}
        >
          {node.label}
        </span>

        {/* Inline spinner while fetching GeoJSON */}
        {isLoading && !isChecked && (
          <div style={{
            width: 10, height: 10, borderRadius: "50%",
            border: "1.5px solid #3b82f6", borderTopColor: "transparent",
            animation: "rhtSpin 0.7s linear infinite", flexShrink: 0,
          }} />
        )}
      </div>

      {/* Children */}
      {hasChildren && expanded && (
        <div>
          {node.children.map((child) => (
            <TreeNode
              key={child.value}
              node={child}
              depth={depth + 1}
              selectedSet={selectedSet}
              loadingSet={loadingSet}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Alert Hint Toast ─────────────────────────────────────────────────────────
const AlertHintToast = ({ onDismiss }) => {
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setExiting(true);
      setTimeout(onDismiss, 320);
    }, 6000);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => { setExiting(true); setTimeout(onDismiss, 320); };

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        bottom: 0,
        right: 40,
        width: 248,
        background: "linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)",
        borderRadius: 9,
        padding: "9px 11px 9px 10px",
        boxShadow: "0 6px 22px rgba(30,58,138,0.38)",
        border: "1px solid rgba(147,197,253,0.35)",
        display: "flex", alignItems: "flex-start", gap: 9,
        animation: exiting
          ? "rhtToastOut 0.32s ease-out forwards"
          : "rhtToastIn 0.25s ease-out",
        zIndex: 60, cursor: "pointer",
      }}
      onClick={dismiss}
    >
      {/* Animated bell */}
      <div style={{
        fontSize: 17, flexShrink: 0, marginTop: 1,
        animation: "rhtBellRing 1.4s ease-in-out infinite",
        transformOrigin: "top center",
      }}>
        🔔
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#bfdbfe", marginBottom: 3 }}>
          Select a Weather Alert
        </div>
      </div>

      <div style={{
        fontSize: 11, color: "rgba(191,219,254,0.6)",
        flexShrink: 0, marginTop: -1, lineHeight: 1,
      }}>✕</div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const RegionHeatmapSelector = ({
  options,            // unused – kept for backward compat
  loading,
  selectedValue,      // legacy single-value string
  selectedValues,     // NEW: Set<string>
  onSelect,
  activeCondition,    // NEW: currently active weather condition key / null
  activeConditionLabel, // NEW: label of the active weather condition
  isOpen,
  onToggle,
  stateId,
  stateName,
}) => {
  const [tree,        setTree]        = useState([]);
  const [treeLoading, setTreeLoading] = useState(false);
  const [loadingSet,  setLoadingSet]  = useState(new Set());
  const [showToast,   setShowToast]   = useState(false);
  const wrapperRef = useRef(null);

  // Normalise to Set regardless of which prop was passed
  const currentSelected = selectedValues instanceof Set
    ? selectedValues
    : selectedValue
      ? new Set([selectedValue])
      : new Set();

  // 1. Reset tree whenever stateId changes (new state selected)
  useEffect(() => {
    setTree([]);
  }, [stateId]);

  // 2. Build tree on open (or immediately if already open after reset)
  useEffect(() => {
    if (!isOpen || !stateId) return;
    if (tree.length > 0) return; // already loaded for this stateId
    setTreeLoading(true);
    buildRegionTree(stateId, stateName)
      .then((t) => setTree(t))
      .catch(() => { })
      .finally(() => setTreeLoading(false));
  }, [isOpen, stateId, stateName, tree.length]);

    // Close on outside click
    useEffect(() => {
        const h = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                if (isOpen) onToggle(); // only call if currently open
            }
        };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, [isOpen, onToggle]);

  // Hide toast when an alert becomes active
  useEffect(() => {
    if (activeCondition) setShowToast(false);
  }, [activeCondition]);

  // ── Toggle a node ──────────────────────────────────────────────────────────
const handleToggle = useCallback(async (node, nowChecked) => {
  const nextSelected = new Set(currentSelected);
  // Track whether we consolidated blocks → district (used for feature fetching)
  let consolidatedToDistrict = null; // will hold the district node if consolidation happens

  if (nowChecked) {
    nextSelected.add(node.value);

    // ── Auto-select all child blocks when a district is selected ──
    if (node.type === "district" && node.children) {
      node.children.forEach((child) => nextSelected.add(child.value));
    }

    // ── Hierarchical exclusion ──
    if (node.type === "district") {
      for (const v of nextSelected) {
        if (v.startsWith("state-")) nextSelected.delete(v);
      }
    } else if (node.type === "block") {
      // Remove state selections
      for (const v of nextSelected) {
        if (v.startsWith("state-")) nextSelected.delete(v);
      }

      // ★ FIX: Check if all sibling blocks are now selected → consolidate to parent district
      const parentDistrict = findParentDistrictInTree(tree, node.value);
      if (parentDistrict && parentDistrict.children && parentDistrict.children.length > 0) {
        const allBlockValues = parentDistrict.children.map((c) => c.value);
        const allSiblingsSelected = allBlockValues.every((v) => nextSelected.has(v));

        if (allSiblingsSelected) {
          // All blocks selected → consolidate to parent district only (clean count)
          allBlockValues.forEach((v) => nextSelected.delete(v));
          nextSelected.add(parentDistrict.value);
          consolidatedToDistrict = parentDistrict;
        } else {
          // Not all siblings selected — ensure parent district is NOT in the set
          if (node.parentGisId != null) {
            nextSelected.delete(`district-${node.parentGisId}`);
          }
        }
      } else {
        // No parent found — original behavior
        if (node.parentGisId != null) {
          nextSelected.delete(`district-${node.parentGisId}`);
        }
      }
    } else if (node.type === "state") {
      // Clear everything then add state + every district + every block
      for (const v of nextSelected) {
        if (!v.startsWith("state-")) nextSelected.delete(v);
      }
      const stateTreeNode = tree.find(n => n.value === node.value);
      if (stateTreeNode) {
        (stateTreeNode.children || []).forEach(district => {
          nextSelected.add(district.value);
          (district.children || []).forEach(block => nextSelected.add(block.value));
        });
      }
    }

  } else {
    // ── Unchecking ──
    const allVals = collectAllValues(node);
    allVals.forEach((v) => nextSelected.delete(v));

    // ★ FIX: When unchecking a block, also remove parent district from selection
    //   and add remaining sibling blocks individually
    if (node.type === "block" && node.parentGisId != null) {
      const hadParentDistrict = nextSelected.has(`district-${node.parentGisId}`);
      nextSelected.delete(`district-${node.parentGisId}`);

      // If parent district was the selected item (not individual blocks),
      // add remaining sibling block values so they stay checked
      if (hadParentDistrict) {
        const parentDistrict = findParentDistrictInTree(tree, node.value);
        if (parentDistrict) {
          (parentDistrict.children || []).forEach((sib) => {
            if (sib.value !== node.value) {
              nextSelected.add(sib.value);
            }
          });
        }
      }
    }

    // ★ FIX: When unchecking a district, also ensure all child blocks are removed
    //   (collectAllValues already handles this, but be explicit)
    if (node.type === "district" && node.children) {
      node.children.forEach((child) => nextSelected.delete(child.value));
    }

  }

  // ── Sync state checkbox for district/block toggles ──────────────────────────
  // When all districts are selected → auto-check state.
  // When any district is missing → auto-uncheck state.
  if (node.type !== "state") {
    syncStateCheckbox(nextSelected, tree);
  }

  if (nowChecked && !activeCondition) setShowToast(true);

  setLoadingSet((prev) => {
    const s = new Set(prev);
    s.add(node.value);
    return s;
  });

  let features = [];

  if (nowChecked) {
    if (node.type === "state") {
      // Fetch all district GeoJSON features in parallel to highlight every district
      const stateTreeNode = tree.find(n => n.value === node.value);
      const districts = stateTreeNode?.children || [];
      const districtFeatures = await Promise.all(
        districts.map(async (district) => {
          try { return await fetchRegionGeoJSON(district); }
          catch (_) { return null; }
        })
      );
      features = districtFeatures.filter(Boolean);
    } else if (consolidatedToDistrict) {
      // ★ FIX: All blocks selected → fetch district GeoJSON instead of individual block
      try {
        const feat = await fetchRegionGeoJSON(consolidatedToDistrict);
        if (feat) features.push(feat);
      } catch (_) {}
      // Pass the district node to onSelect so DashboardMap handles it correctly
      onSelect(nextSelected, features, nowChecked, consolidatedToDistrict);
      setLoadingSet((prev) => {
        const s = new Set(prev);
        s.delete(node.value);
        return s;
      });
      return;
    } else {
      // Fetch GeoJSON for the newly checked node
      try {
        const feat = await fetchRegionGeoJSON(node);
        if (feat) features.push(feat);
      } catch (_) {}
    }

  } else if (node.type === "block" && node.parentGisId != null) {
    // ★ FIX: When unchecking a block, fetch GeoJSON for each remaining
    //   checked sibling block so the map can show them individually
    //   (replacing the parent district's single feature).
    const parentDistrict = findParentDistrictInTree(tree, node.value);
    if (parentDistrict) {
      const remainingSiblings = (parentDistrict.children || []).filter(
        (b) => b.value !== node.value && nextSelected.has(b.value)
      );
      const siblingFeatures = await Promise.all(
        remainingSiblings.map(async (sib) => {
          try {
            return await fetchRegionGeoJSON(sib);
          } catch (_) {
            return null;
          }
        })
      );
      features = siblingFeatures.filter(Boolean);
    }
  }

  onSelect(nextSelected, features, nowChecked, node);

  setLoadingSet((prev) => {
    const s = new Set(prev);
    s.delete(node.value);
    return s;
  });
}, [currentSelected, activeCondition, onSelect, tree, stateId, stateName]);

  // ── Clear all ──────────────────────────────────────────────────────────────
  const handleClear = (e) => {
    e.stopPropagation();
    onSelect(new Set(), [], false, null);
    setShowToast(false);
  };

  // ── Trigger button label ───────────────────────────────────────────────────
  const selCount = currentSelected.size;
  const getTriggerLabel = () => {
    if (loading) return "Loading regions…";
    if (selCount === 0) return "Region Hierarchy";
    if (selCount === 1) {
      const val = [...currentSelected][0];
      const find = (nodes) => {
        for (const n of nodes) {
          if (n.value === val) return n.label;
          const f = find(n.children || []);
          if (f) return f;
        }
        return null;
      };
      return find(tree) || val;
    }
    return `${selCount} regions selected`;
  };

  return (
    <div ref={wrapperRef} style={{ position: "relative", zIndex: 0, top: 83,}}>
      <style>{TREE_STYLES}</style>

          {/* ── Trigger button (icon only) ── */}
          <div
              onClick={() => onToggle()}
              title="Region Hierarchy"
              style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  background: "var(--od-card-bg-translucent)",
                  border: selCount > 0
                      ? "1.5px solid #3b82f6"
                      : "1px solid rgba(0,0,0,0.18)",
                  boxShadow: selCount > 0
                      ? "0 2px 12px rgba(59,130,246,0.25)"
                      : "0 2px 6px rgba(0,0,0,0.12)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.15s",
                  position: "relative",
              }}
          >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path
                      d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
                      fill="#1f57a5"
                  />
                  <circle cx="12" cy="9" r="2.5" fill="white" />
              </svg>
          </div>

      {/* ── Tree panel ── */}
      {isOpen && (
        <div style={{
          position: "absolute", top: 0, right: 41,
          width: 248,
          background: "var(--od-card-bg-translucent)",
          borderRadius: 10,
          boxShadow: "0 8px 32px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.06)",
          border: "1px solid var(--od-border)",
          animation: "rhtSlideIn 0.15s ease-out",
          overflow: "hidden",
        }}>

          {/* Header */}
          <div style={{
            padding: "8px 12px 7px",
            borderBottom: "1px solid var(--od-border)",
            display: "flex", alignItems: "center", gap: 6,
            background: "linear-gradient(135deg, var(--od-surface-alt), var(--od-card-bg))",
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
              <path d="M3 6h18M3 12h18M3 18h18" stroke="#3b82f6" strokeWidth="2"
                strokeLinecap="round" />
            </svg>
            <span style={{ fontSize: 11, fontWeight: 700, color: "var(--od-text-primary)", letterSpacing: "0.04em" }}>
              Region Hierarchy
            </span>

            {selCount > 0 && (
              <span
                onClick={handleClear}
                style={{
                  marginLeft: "auto", fontSize: 10, color: "#ef4444",
                  cursor: "pointer", fontWeight: 600,
                  padding: "1px 7px", borderRadius: 4,
                  border: "1px solid #fecaca", background: "#fef2f2",
                  flexShrink: 0, transition: "background 0.12s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#fee2e2")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#fef2f2")}
              >
                ✕ Clear all
              </span>
            )}

            <button
              onClick={() => onToggle()}
              style={{
                marginLeft: selCount > 0 ? 4 : "auto",
                width: 20, height: 20, borderRadius: 4,
                border: "none", background: "var(--od-surface-alt)",
                cursor: "pointer", display: "flex",
                alignItems: "center", justifyContent: "center",
                fontSize: 11, color: "var(--od-text-muted)", flexShrink: 0,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--od-border)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "var(--od-surface-alt)")}
            >✕</button>
          </div>

          {/* Tree body */}
          <div style={{
            maxHeight: 300, overflowY: "auto",
            padding: "6px 10px 8px",
            scrollbarWidth: "thin",
            scrollbarColor: "#d1d5db transparent",
          }}>
            {(loading || treeLoading) ? (
              <div style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "14px 0", justifyContent: "center",
                color: "var(--od-text-muted)", fontSize: 11,
              }}>
                <div style={{
                  width: 14, height: 14, borderRadius: "50%",
                  border: "2px solid #3b82f6", borderTopColor: "transparent",
                  animation: "rhtSpin 0.7s linear infinite",
                }} />
                Loading regions…
              </div>
            ) : tree.length === 0 ? (
              <div style={{ padding: "12px 0", textAlign: "center", fontSize: 11, color: "var(--od-text-muted)" }}>
                No regions found
              </div>
            ) : (
              tree.map((node) => (
                <TreeNode
                  key={node.value}
                  node={node}
                  depth={0}
                  selectedSet={currentSelected}
                  loadingSet={loadingSet}
                  onToggle={handleToggle}
                />
              ))
            )}
          </div>

          {/* Footer — context-aware hint */}
          <div style={{
            padding: "6px 12px 7px",
            borderTop: "1px solid var(--od-border)",
            background: activeCondition ? "#f0fdf4" : selCount > 0 ? "#fffbeb" : "var(--od-surface-alt)",
            display: "flex", alignItems: "flex-start", gap: 5,
          }}>
            <span style={{ fontSize: 11, flexShrink: 0, marginTop: 0.5 }}>
              {activeCondition ? "✅" : selCount > 0 ? "⚠️" : "ℹ️"}
            </span>
            <span style={{
              fontSize: 9.5, lineHeight: 1.45,
              color: activeCondition ? "#15803d" : selCount > 0 ? "#92400e" : "var(--od-text-muted)",
              fontWeight: (selCount > 0 && !activeCondition) ? 600 : 400,
            }}>
              {activeCondition
                ? `🌡️ ${activeConditionLabel || activeCondition} active on ${selCount} region${selCount > 1 ? "s" : ""}`
                : selCount > 0
                  ? "Select a Weather Alert 🔔 to display the heatmap for this region"
                  : "Click a region to filter heatmap to its boundary"}
            </span>
          </div>
        </div>
      )}

      {/* ── Floating toast: shown when regions selected but no alert active ── */}
      {showToast && !activeCondition && (
        <AlertHintToast onDismiss={() => setShowToast(false)} />
      )}
    </div>
  );
};

export default RegionHeatmapSelector;

